// BUG-21 — Per-route help content for the "?" help button.
//
// Keyed by routeKey (e.g. "admin.users", "admin.notifs"). Pages pass their own
// routeKey to <T2THelpButton/>. Content is plain English, ~3 sentences each,
// plus 2–4 key-action bullets. Optional videoUrl is rendered as a "Watch a
// short walkthrough" link.
//
// To author new entries: add a new key here, drop content. No backend change.
// To localise later: this whole file becomes the en/ entry of an i18n bundle.

export const HELP_CONTENT = {
  // ─── Admin tabs ─────────────────────────────────────────────────────────────
  "admin.roles": {
    title: "Roles",
    body:
      "Roles are bundles of permissions. Every user in T2T is assigned exactly one role, " +
      "which decides what they can see and do across the platform.",
    actions: [
      "Click + Add Role to create a new role with a custom permission set.",
      "Toggle individual permissions inside a role — changes apply to every user with that role.",
      "Use the colour swatch so the role is easy to spot in lists, kanbans and reports.",
    ],
  },
  "admin.users": {
    title: "Users",
    body:
      "All people who can sign in to T2T. Each user has a role, an org unit (group → company → division → department), " +
      "an optional reporting manager, and channel preferences for notifications.",
    actions: [
      "+ Add User to create one user manually.",
      "↑ Import CSV to bulk-create users from a spreadsheet — download the sample first.",
      "Click any user to edit their org assignment, reporting manager or status.",
    ],
  },
  "admin.vendor_roles": {
    title: "Vendor Role Assignments",
    body:
      "Decide which T2T users handle which vendor approval steps. Each role (Vendor Initiator, Vendor Checker, " +
      "Finance Data Controller, MDM Approver) maps to one or more users.",
    actions: [
      "Click any role tile to assign or unassign users.",
      "An unfilled role blocks any vendor request from advancing past that stage.",
      "Re-assign at any time — in-flight requests pick up the new approver instantly.",
    ],
  },
  "admin.request_types": {
    title: "Request Types",
    body:
      "A request type defines the form a requester fills in. Workflows process requests of a given type — so the " +
      "fields you define here become the data your approvers see.",
    actions: [
      "+ Add Request Type to define a new form (e.g. Vendor Creation, Purchase Order, Capex).",
      "Drag fields with the + Insert here dividers; rename, mark required, add validators.",
      "Dropdown fields use a row editor for options — Label + Value per row.",
    ],
  },
  "admin.vendor_signups": {
    title: "Vendor Sign-ups",
    body:
      "Vendors who self-registered via the public /vendor-signup page. Approving emails them a secure portal link " +
      "to complete the full onboarding form (PAN, GST, bank, KYC).",
    actions: [
      "Copy the sign-up link from the panel above to share with new vendors.",
      "👁 View details to see the full submission before deciding.",
      "Approve to send the portal link, or Reject to remove the request.",
    ],
  },
  "admin.screen_groups": {
    title: "Screen Groups (Vendor Onboarding Form Layout)",
    body:
      "These six groups are the SAP-aligned tabs vendors see in the post-approval onboarding form. Click any group " +
      "to expand and add, edit, reorder or deactivate the fields inside it.",
    actions: [
      "Click a group row to expand and see its fields.",
      "Use ↑↓ to reorder fields, ✎ to edit, − to deactivate (vendor stops seeing it).",
      "These tabs are vendor-specific — for other modules (PO, Capex, etc.) use Request Types instead.",
    ],
  },
  "admin.field_access": {
    title: "Field Access",
    body:
      "Per-stage rules for which form fields are visible or editable. Useful for hiding sensitive fields from earlier " +
      "approvers, or locking fields after they've been signed off.",
    actions: [
      "Pick a workflow + stage to scope the rule.",
      "Mark fields as Visible / Editable / Hidden — non-listed fields default to Visible+Editable.",
      "Rules apply at runtime — existing requests see updated rules immediately.",
    ],
  },
  "admin.lookups": {
    title: "Lookup Values",
    body:
      "Master data lists used by every form: Company Codes, Plant Codes, Payment Terms, Delivery Terms, Account Groups, " +
      "Currencies. Most are SAP-aligned and rarely change.",
    actions: [
      "Pick a tab, click + Add to add a new code.",
      "For Currencies, the Country picker auto-fills code + label + symbol.",
      "Toggle a row inactive instead of deleting if it might be referenced by old requests.",
    ],
  },
  "admin.sla_config": {
    title: "SLA Config",
    body:
      "A bulk-edit grid for stage-level SLA times across every active workflow. Same data as the Workflow Builder — " +
      "edits here are reflected everywhere immediately.",
    actions: [
      "✎ Edit any row to set SLA in minutes (0 or blank = no SLA).",
      "Warning fires at 80% elapsed, breach at 100%, manager-escalation at 150%.",
      "For complex multi-branch workflows, use the Workflow Builder for full visibility.",
    ],
  },
  "admin.notifs": {
    title: "Notifications",
    body:
      "Templates for every event the platform emits — request submitted, awaiting approval, approved, rejected, SLA " +
      "warning, SLA breach, and any custom events you add. Use {{variable}} placeholders for dynamic content.",
    actions: [
      "+ Add Notification to create a new event template (event type + label + channel).",
      "Click the channel pill on any card to switch in-app / email / WhatsApp / SMS.",
      "Save Templates writes all changes in one bulk update.",
    ],
  },
  "admin.channels": {
    title: "Channels",
    body:
      "How notifications actually leave T2T — email (SendGrid/Postmark/SMTP/SES), WhatsApp (WATI/Twilio/Gupshup), " +
      "SMS (Twilio/MSG91), or in-app bell. Each channel type has one row; activating it routes all relevant templates " +
      "through that provider.",
    actions: [
      "+ Add Channel or click an existing row to set provider + API credentials + sender address.",
      "Test sends a sample message so you can verify credentials before going live.",
      "Disable to stop a channel without losing its config.",
    ],
  },
  "admin.integrations": {
    title: "Integrations",
    body:
      "External system endpoints T2T calls during a workflow — SAP master-data sync, Ariba push, Fiori sync, PAN/GST " +
      "validators, three-way matching. Each integration has its own credentials block and per-call audit log.",
    actions: [
      "Configure each integration's base URL + credentials + active toggle.",
      "Watch the call log to see live successes/retries/failures.",
      "Failed calls are retried every 5 minutes for up to 24 hours, then surfaced as alerts.",
    ],
  },
  "admin.guidance": {
    title: "Guidance Analytics",
    body:
      "How users are engaging with the in-app guidance system — Welcome modal, Wizard, Tutorial, Templates and " +
      "contextual hints. Use this to spot which steps users abandon and where extra help is needed.",
    actions: [
      "Wizard Step Drop-off shows where users quit the guided workflow build.",
      "Tile counters track first-time engagement across the org.",
      "Per-feature guidance for non-workflow areas is on the roadmap (T2T-GUIDE-EXPANSION).",
    ],
  },
};

// Lookup helper — returns null when no entry exists, so the button can hide itself.
export const getHelpContent = (routeKey) => HELP_CONTENT[routeKey] || null;

const helpContentModule = { HELP_CONTENT, getHelpContent };
export default helpContentModule;
